import { useDispatch, useSelector } from 'react-redux';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useConfirmModal from '../useConfirmModal';
import { RootState } from '../../store/store';
import {
    closeAddProductModal,
    closeEditProductModal,
} from '../../store/slices/sale/consumerFinalSaleSlice';
import {
    setSaleProducts,
    updateSaleTotals,
    updateSaleTotalsView
} from '../../store/slices/sale/saleSlice';
import { useSale } from './useSale';
import ConsumerFinalSaleService from '../../services/sale/ConsumerFinalSaleService';
import useApiErrorHandler from '../useApiErrorHandler';

export const useConsumerFinalSale = () => {
    const dispatch = useDispatch();
    const { show: showAlert } = useAlerts();
    const confirm = useConfirmModal();
    const { handle } = useApiErrorHandler();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

    const {
        handlerAddProduct,
        handlerUpdateProduct,
        handlerDeleteProduct,
        handlerSelectProduct,
    } = useSale();

    const { showAddProductModal, showEditProductModal } = useSelector(
        (state: RootState) => state.consumerFinalSale
    );

    const handlerAddConsumerFinalProduct = async (product: any) => {
        try {
            showLoadingIndicator();
            const response = await ConsumerFinalSaleService.addProduct(product);

            if (response.statusCode === 201) {
                const { title, message, type, data } = response;
                const { product: savedProduct, products, lines, totals, totalsView } = data;
                const recalculatedProducts = products ?? lines;

                if (recalculatedProducts) dispatch(setSaleProducts(recalculatedProducts));
                else handlerAddProduct(savedProduct);
                dispatch(updateSaleTotals(totals));
                dispatch(updateSaleTotalsView(totalsView));
                dispatch(closeAddProductModal());
                showAlert(type, title, message);
            } else {
                showAlert('error', 'Error', 'No se pudo agregar el producto.');
            }
        } catch (error: any) {
            const status = error?.response?.data?.statusCode || error?.response?.status;

            if (status === 409 && !product?.confirmReplaceGlobalDiscount) {
                confirm({
                    title: '¿Agregar descuento manual?',
                    message:
                        error?.response?.data?.message ||
                        'La venta tiene un descuento global activo. Poner un descuento manual en esta línea lo va a quitar.',
                    icon: 'warning',
                    confirmText: 'Sí, quitar el global',
                    cancelText: 'Cancelar',
                    onConfirm: () =>
                        handlerAddConsumerFinalProduct({
                            ...product,
                            confirmReplaceGlobalDiscount: true,
                        }),
                });
                return;
            }

            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    const handlerUpdateConsumerFinalProduct = async (product: any) => {
        try {
            showLoadingIndicator();
            const response = await ConsumerFinalSaleService.updateProduct(product);
            if (response.statusCode === 200) {
                const { title, message, type, data } = response;
                const { product: updatedProduct, products, lines, totals, totalsView } = data;
                const recalculatedProducts = products ?? lines;

                if (recalculatedProducts) dispatch(setSaleProducts(recalculatedProducts));
                else handlerUpdateProduct(updatedProduct);
                dispatch(updateSaleTotals(totals));
                dispatch(updateSaleTotalsView(totalsView));
                dispatch(closeEditProductModal());
                // openModal(type, { title, message });
                showAlert(type, title, message);
            } else {
                showAlert('error', 'Error', 'No se pudo actualizar el producto.');
            }
        } catch (error: any) {
            const status = error?.response?.data?.statusCode || error?.response?.status;

            if (status === 409 && !product?.confirmReplaceGlobalDiscount) {
                confirm({
                    title: '¿Agregar descuento manual?',
                    message:
                        error?.response?.data?.message ||
                        'La venta tiene un descuento global activo. Poner un descuento manual en esta línea lo va a quitar.',
                    icon: 'warning',
                    confirmText: 'Sí, quitar el global',
                    cancelText: 'Cancelar',
                    onConfirm: () =>
                        handlerUpdateConsumerFinalProduct({
                            ...product,
                            confirmReplaceGlobalDiscount: true,
                        }),
                });
                return;
            }

            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    const handlerDeleteConsumerFinalProduct = async (id: number) => {
        confirm({
            title: '¿Eliminar Producto?',
            message: '¿Estás seguro que deseas eliminar el producto?',
            icon: 'warning',
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            confirmArgs: id,
            onConfirm: async (id) => {
                try {
                    showLoadingIndicator();
                    const response = await ConsumerFinalSaleService.deleteProduct(id);
                    if (response.statusCode === 200) {
                        const { title, message, type, data } = response;
                        if (data?.products ?? data?.lines) {
                            dispatch(setSaleProducts(data.products ?? data.lines));
                        }
                        else handlerDeleteProduct(id);
                        dispatch(updateSaleTotals(data?.totals));
                        dispatch(updateSaleTotalsView(data?.totalsView));
                        // openModal(type, { title, message });
                        showAlert(type, title, message);
                    } else {
                        showAlert('error', 'Error', 'No se pudo eliminar el producto.');
                    }
                } catch (error: any) {
                    handle(error);
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };

    const handlerGetProductById = async (detailId: number) => {
        try {
            showLoadingIndicator();
            const response = await ConsumerFinalSaleService.getProductById(detailId);
            if (response.statusCode === 200) {
                handlerSelectProduct(response.data);
                return response.data;
            } else {
                showAlert('error', 'Error', 'No se pudo obtener el producto.');
            }
        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    return {
        handlerAddConsumerFinalProduct,
        handlerUpdateConsumerFinalProduct,
        handlerDeleteConsumerFinalProduct,
        handlerGetProductById,
        showAddProductModal,
        showEditProductModal,
    };
};
